import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { customerAPI, invoicesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';
import { LuFileText, LuCalendar, LuRefreshCw, LuPencil, LuX, LuUpload, LuImage } from 'react-icons/lu';
import { FEE_CATEGORIES, getFeeMetadata, STORAGE_CLASSES } from '../../utils/customerFeeCatalog';

// Customer-facing Company page. Organizes company-level settings into tabs:
//   - Company Info     (address, phone, billing contacts, notification routing)
//   - Email Preferences (company-level notification toggles)
//   - Default Receiving Instructions
//   - Contracts        (admin only)
//   - Billing          (admin only, read-only fee schedule)
//   - Warehouses       (internal warehouses the customer has access to)
//
// RBAC summary (enforced on both the UI and backend):
//   - Editing anything on this page requires customer_admin role.
//   - Regular users can view the Company Info, Email Preferences, Default
//     Receiving Instructions, and Warehouses tabs; the Contracts and
//     Billing tabs are hidden for them.
//
// Read-only-by-default UX: the Company Info, Email Preferences, and Default
// Receiving Instructions tabs render their inputs as disabled until the
// admin clicks "Edit". This prevents accidental edits while clicking around.

const CONTRACT_TYPES = {
  service_agreement: 'Service Agreement',
  storage: 'Storage Agreement',
  volume_discount: 'Volume Discount',
  nda: 'NDA',
  other: 'Other'
};

const STATUS_BADGES = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  terminated: 'bg-gray-100 text-gray-800'
};

const NOTIFICATION_KEYS = [
  ['request_created', 'Request Created'],
  ['request_status', 'Request Status Updates'],
  ['invoice_created', 'New Invoice'],
  ['invoice_status', 'Invoice Status Updates'],
  ['manifest_updates', 'Manifest / Inbound Updates'],
  ['crossdock_updates', 'Cross-Dock Updates'],
  ['receiving_updates', 'Receiving Updates'],
  ['shipment_updates', 'Shipment Updates']
];

const RECEIVING_INSTRUCTIONS = [
  ['require_serial_numbers_on_receive', 'Require serial numbers at receiving'],
  ['require_mac_addresses_on_receive', 'Require MAC addresses at receiving'],
  ['relabel_on_receive', 'Default to relabel on receipt']
];

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  const n = Number(value);
  if (Number.isNaN(n)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const humanize = (value) => {
  if (!value) return '';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const emptyCompanyForm = () => ({
  companyName: '',
  primaryContactName: '',
  primaryContactEmail: '',
  primaryContactPhone: '',
  phone: '',
  address: { street: '', addressLine2: '', city: '', state: '', postalCode: '', country: 'Canada' },
  notificationEmail: '',
  notificationEmailsText: '',
  billingInfo: {
    billingContactName: '',
    billingContactEmail: '',
    billingContactPhone: ''
  },
  notificationPreferences: {},
  receivingPreferences: {
    require_serial_numbers_on_receive: false,
    require_mac_addresses_on_receive: false,
    relabel_on_receive: false
  }
});

const hydrateCompanyForm = (data) => ({
  companyName: data.companyName || data.company?.name || '',
  primaryContactName: data.contactName || data.user?.name || '',
  primaryContactEmail: data.email || data.user?.email || '',
  primaryContactPhone: data.contactPhone || data.user?.phone || '',
  phone: data.phone || '',
  address: {
    street: data.address?.street || '',
    addressLine2: data.address?.addressLine2 || '',
    city: data.address?.city || '',
    state: data.address?.state || '',
    postalCode: data.address?.postalCode || '',
    country: data.address?.country || 'Canada'
  },
  notificationEmail: data.notificationEmail || '',
  notificationEmailsText: Array.isArray(data.notificationEmails) ? data.notificationEmails.join('\n') : '',
  billingInfo: {
    billingContactName: data.billingInfo?.billingContactName || '',
    billingContactEmail: data.billingInfo?.billingContactEmail || '',
    billingContactPhone: data.billingInfo?.billingContactPhone || ''
  },
  notificationPreferences: data.notificationPreferences || {},
  receivingPreferences: {
    require_serial_numbers_on_receive: !!data.receivingPreferences?.require_serial_numbers_on_receive,
    require_mac_addresses_on_receive: !!data.receivingPreferences?.require_mac_addresses_on_receive,
    relabel_on_receive: !!data.receivingPreferences?.relabel_on_receive
  }
});

const CustomerProfile = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // customer_admin is the minimum "admin" role for editing and for the
  // Contracts/Billing tabs. Authoritative check is on the backend; this is
  // only used to gate the UI affordances.
  const isCustomerAdmin = Array.isArray(user?.roles) && user.roles.includes('customer_admin');

  const [activeTab, setActiveTab] = useState('company');
  const [editMode, setEditMode] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm());
  const [savingCompany, setSavingCompany] = useState(false);
  const [packingSlipLogoFile, setPackingSlipLogoFile] = useState(null);
  const [removePackingSlipLogo, setRemovePackingSlipLogo] = useState(false);
  const [savingPackingSlipLogo, setSavingPackingSlipLogo] = useState(false);
  const [packingSlipLogoPreviewUrl, setPackingSlipLogoPreviewUrl] = useState('');

  const {
    data: profile,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['customer-company-profile'],
    queryFn: async () => {
      const response = await customerAPI.getProfile();
      return response.data || response;
    },
    retry: 1
  });

  useEffect(() => {
    if (profile) {
      setCompanyForm(hydrateCompanyForm(profile));
    }
  }, [profile]);

  // Only fetch these when the corresponding tab is active AND the user has
  // access. This avoids extra round-trips and 403 noise.
  const { data: contractsData, isLoading: contractsLoading } = useQuery(
    ['customer-contracts-account'],
    () => customerAPI.getContracts(),
    { staleTime: 300000, enabled: isCustomerAdmin && activeTab === 'contracts' }
  );

  const { data: feesData, isLoading: feesLoading } = useQuery(
    ['customer-my-fees'],
    () => customerAPI.getMyFees(),
    { staleTime: 300000, enabled: isCustomerAdmin && activeTab === 'billing' }
  );

  // Pull the storage snapshot too so the Billing tab can detect
  // whether the customer is on a flat-rate storage agreement. When
  // flat-rate is active, we hide the per-pallet storage rate table
  // and show the agreement card instead.
  const { data: storageSnapshot } = useQuery(
    ['customer-storage-snapshot-billing'],
    () => invoicesAPI.getStorageSnapshot(),
    { staleTime: 300000, enabled: isCustomerAdmin && activeTab === 'billing' }
  );
  const isFlatRateStorage = storageSnapshot?.flatRate === true;
  const storageAgreement = storageSnapshot?.agreement || null;

  const { data: accessibleWarehousesData, isLoading: warehousesLoading } = useQuery(
    ['customer-accessible-warehouses'],
    () => customerAPI.getAccessibleWarehouses(),
    { staleTime: 300000, enabled: activeTab === 'warehouses' }
  );

  const { data: packingSlipSettingsData, isLoading: packingSlipSettingsLoading } = useQuery(
    ['customer-packing-slip-settings'],
    () => customerAPI.getPackingSlipSettings(),
    { staleTime: 300000, enabled: activeTab === 'company' }
  );

  const contracts = contractsData?.data || [];
  const fees = feesData?.data || [];
  const accessibleWarehouses = accessibleWarehousesData?.data || [];
  // packingSlipSettingsData is the raw axios response; the actual
  // payload is nested under .data.
  const packingSlipPayload = packingSlipSettingsData?.data || {};
  const packingSlipSettings = packingSlipPayload.settings || {};
  const packingSlipLogoUrl = packingSlipLogoPreviewUrl || packingSlipSettings?.logo_url || null;

  useEffect(() => {
    let objectUrl = '';

    const loadLogo = async () => {
      if (!packingSlipPayload.has_logo_file || removePackingSlipLogo) {
        setPackingSlipLogoPreviewUrl('');
        return;
      }

      try {
        const logoBlob = await customerAPI.getPackingSlipLogo();
        objectUrl = URL.createObjectURL(logoBlob);
        setPackingSlipLogoPreviewUrl(objectUrl);
      } catch (error) {
        setPackingSlipLogoPreviewUrl('');
      }
    };

    loadLogo();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [packingSlipPayload.has_logo_file, packingSlipPayload.logo_updated_at, removePackingSlipLogo]);

  // Group fees by warehouse, then within each warehouse by catalog
  // category. Rows whose fee_type belongs to a hidden (legacy)
  // catalog category are filtered OUT of the customer-facing Billing
  // tab entirely -- the customer only sees the active fee list. If
  // a customer has legacy rows from an older rate card, they can
  // still be audited from the admin side; they just don't render
  // here to avoid confusion about what's billable.
  const feesByWarehouse = useMemo(() => {
    const visibleCategoryOrder = FEE_CATEGORIES.filter((c) => !c.hidden).map((c) => c.label);
    const warehouseMap = new Map();

    for (const fee of fees) {
      const meta = getFeeMetadata(fee.fee_type);
      // Skip legacy / hidden category rows on the customer-facing page.
      if (meta?.hiddenCategory) continue;

      const whKey = fee.warehouse_id || 'unassigned';
      if (!warehouseMap.has(whKey)) {
        warehouseMap.set(whKey, {
          warehouseName: fee.Warehouse?.name || 'Unassigned',
          warehouseCode: fee.Warehouse?.code || null,
          categories: new Map()
        });
      }
      const wh = warehouseMap.get(whKey);

      const categoryLabel = meta?.category || 'Other';
      if (!wh.categories.has(categoryLabel)) {
        wh.categories.set(categoryLabel, []);
      }
      wh.categories.get(categoryLabel).push({
        ...fee,
        _meta: meta,
        _label: meta?.label || humanize(fee.fee_type),
        _description: meta?.description || null
      });
    }

    return Array.from(warehouseMap.values()).map((wh) => {
      const orderedCategories = [];
      for (const catLabel of visibleCategoryOrder) {
        if (wh.categories.has(catLabel)) {
          const rows = wh.categories.get(catLabel);
          rows.sort((a, b) => (a._label || '').localeCompare(b._label || ''));
          orderedCategories.push({ label: catLabel, rows });
          wh.categories.delete(catLabel);
        }
      }
      // Unknown categories (shouldn't happen under the simplified
      // catalog but keep the fallback) append last.
      for (const [catLabel, rows] of wh.categories) {
        orderedCategories.push({ label: catLabel, rows });
      }
      return {
        warehouseName: wh.warehouseName,
        warehouseCode: wh.warehouseCode,
        categories: orderedCategories
      };
    });
  }, [fees]);

  const handleCompanyFieldChange = (field, value) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setCompanyForm((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const handleBillingInfoChange = (field, value) => {
    setCompanyForm((prev) => ({
      ...prev,
      billingInfo: { ...prev.billingInfo, [field]: value }
    }));
  };

  const handleNotificationPrefChange = (field, checked) => {
    setCompanyForm((prev) => ({
      ...prev,
      notificationPreferences: { ...prev.notificationPreferences, [field]: checked }
    }));
  };

  const handleReceivingPrefChange = (field, checked) => {
    setCompanyForm((prev) => ({
      ...prev,
      receivingPreferences: { ...prev.receivingPreferences, [field]: checked }
    }));
  };

  const handleCancelEdit = () => {
    if (profile) {
      setCompanyForm(hydrateCompanyForm(profile));
    }
    setEditMode(false);
  };

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      await customerAPI.updateProfile({
        name: companyForm.companyName,
        phone: companyForm.phone,
        address: companyForm.address,
        notificationEmail: companyForm.notificationEmail,
        notificationEmails: companyForm.notificationEmailsText
          .split('\n')
          .map((email) => email.trim())
          .filter(Boolean),
        billingInfo: companyForm.billingInfo,
        notificationPreferences: companyForm.notificationPreferences,
        receivingPreferences: companyForm.receivingPreferences
      });
      toast.success('Company settings updated');
      setEditMode(false);
      queryClient.invalidateQueries(['customer-company-profile']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update company settings');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleSavePackingSlipLogo = async () => {
    if (!packingSlipLogoFile && !removePackingSlipLogo) {
      toast.info('Choose a logo file first.');
      return;
    }

    setSavingPackingSlipLogo(true);
    try {
      await customerAPI.updatePackingSlipSettings({
        settings: {},
        ...(packingSlipLogoFile ? { logo_file: packingSlipLogoFile } : {}),
        ...(removePackingSlipLogo ? { remove_logo: true } : {})
      });
      toast.success('Packing slip logo updated');
      setPackingSlipLogoFile(null);
      setRemovePackingSlipLogo(false);
      queryClient.invalidateQueries(['customer-packing-slip-settings']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update packing slip logo');
    } finally {
      setSavingPackingSlipLogo(false);
    }
  };

  // Generic disabled state helper. When not in edit mode, every input on
  // this page is read-only. Regular (non-admin) users never get edit mode.
  const readOnly = !editMode || !isCustomerAdmin;
  const inputClass = (extra = '') =>
    `w-full rounded-md border-gray-300 shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm ${readOnly ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''} ${extra}`;

  const tabs = [
    { id: 'company', label: 'Company Info' },
    { id: 'email', label: 'Email Preferences' },
    { id: 'receiving', label: 'Default Receiving' },
    ...(isCustomerAdmin ? [{ id: 'contracts', label: 'Contracts' }] : []),
    ...(isCustomerAdmin ? [{ id: 'billing', label: 'Billing' }] : []),
    { id: 'warehouses', label: 'Warehouses' }
  ];

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorAlert message={error?.message || 'Error loading company settings'} />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-brand-navy">Company</h1>
          <p className="mt-1 text-sm text-gray-500">
            Company-level settings, contracts, billing rates, and warehouse
            access.
          </p>
        </div>

        {isCustomerAdmin && ['company', 'email', 'receiving'].includes(activeTab) && (
          <div className="flex items-center gap-2">
            {!editMode ? (
              <button
                type="button"
                onClick={() => setEditMode(true)}
                className="inline-flex items-center gap-2 rounded-md border border-brand-navy px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-navy/5"
              >
                <LuPencil className="h-4 w-4" /> Edit
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <LuX className="h-4 w-4" /> Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveCompany}
                  disabled={savingCompany}
                  className="rounded-md border border-brand-gold bg-brand-gold px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-gold/90 disabled:opacity-50"
                >
                  {savingCompany ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-x-6" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'receiving' && isCustomerAdmin) {
                  setEditMode(true);
                } else if (tab.id !== 'receiving' && editMode) {
                  setEditMode(false);
                }
              }}
              className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-brand-gold text-brand-navy'
                  : 'border-transparent text-gray-500 hover:text-brand-navy hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'company' && (
        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div>
            <h2 className="text-lg font-medium text-brand-navy">Company Settings</h2>
            <p className="mt-1 text-sm text-gray-500">
              Operational contact information, company address, billing
              contacts, and notification routing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                disabled={readOnly}
                value={companyForm.companyName}
                onChange={(e) => handleCompanyFieldChange('companyName', e.target.value)}
                className={inputClass()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Contact</label>
              <input
                disabled
                value={companyForm.primaryContactName}
                className={inputClass('bg-gray-50 text-gray-700')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Phone</label>
              <input
                disabled
                value={companyForm.primaryContactPhone}
                className={inputClass('bg-gray-50 text-gray-700')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Email</label>
              <input
                disabled
                value={companyForm.primaryContactEmail}
                className={inputClass('bg-gray-50 text-gray-700')}
              />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-brand-navy mb-3">Company Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input
                  disabled={readOnly}
                  value={companyForm.address.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input
                  disabled={readOnly}
                  value={companyForm.address.addressLine2}
                  onChange={(e) => handleAddressChange('addressLine2', e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  disabled={readOnly}
                  value={companyForm.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                <input
                  disabled={readOnly}
                  value={companyForm.address.state}
                  onChange={(e) => handleAddressChange('state', e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                <input
                  disabled={readOnly}
                  value={companyForm.address.postalCode}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  disabled={readOnly}
                  value={companyForm.address.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)}
                  className={inputClass()}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-brand-navy mb-3">Billing Contacts</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Contact Name</label>
                <input
                  disabled={readOnly}
                  value={companyForm.billingInfo.billingContactName}
                  onChange={(e) => handleBillingInfoChange('billingContactName', e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Contact Email</label>
                <input
                  disabled={readOnly}
                  value={companyForm.billingInfo.billingContactEmail}
                  onChange={(e) => handleBillingInfoChange('billingContactEmail', e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Contact Phone</label>
                <input
                  disabled={readOnly}
                  value={companyForm.billingInfo.billingContactPhone}
                  onChange={(e) => handleBillingInfoChange('billingContactPhone', e.target.value)}
                  className={inputClass()}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-brand-navy mb-3">Notification Routing</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Notification Email</label>
                <input
                  disabled={readOnly}
                  value={companyForm.notificationEmail}
                  onChange={(e) => handleCompanyFieldChange('notificationEmail', e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div className="md:col-start-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notification Emails</label>
                <textarea
                  rows="4"
                  disabled={readOnly}
                  value={companyForm.notificationEmailsText}
                  onChange={(e) => handleCompanyFieldChange('notificationEmailsText', e.target.value)}
                  placeholder="One email per line"
                  className={inputClass()}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-brand-navy mb-3">Packing Slip Logo</h3>
            <div className="rounded-lg border border-gray-200 p-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Upload Logo</p>
                <p className="mt-1 text-sm text-gray-500">
                  Logo is used for custom packing slips. Optional.
                </p>
              </div>

              <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-start">
                <div className="flex h-24 w-40 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50">
                  {packingSlipSettingsLoading ? (
                    <LoadingSpinner />
                  ) : packingSlipLogoUrl && !removePackingSlipLogo ? (
                    <img
                      src={packingSlipLogoUrl}
                      alt="Packing slip logo"
                      className="max-h-20 max-w-36 object-contain"
                    />
                  ) : packingSlipLogoFile ? (
                    <div className="text-center">
                      <LuImage className="mx-auto h-6 w-6 text-gray-400" />
                      <p className="mt-1 text-xs text-gray-500">{packingSlipLogoFile.name}</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <LuImage className="mx-auto h-6 w-6 text-gray-400" />
                      <p className="mt-1 text-xs text-gray-500">No logo uploaded</p>
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-3">
                  {isCustomerAdmin ? (
                    <>
                      <div>
                        <label className="inline-flex cursor-pointer items-center rounded-md border border-brand-navy px-3 py-2 text-sm font-medium text-brand-navy hover:bg-brand-navy/5">
                          <LuUpload className="mr-2 h-4 w-4" />
                          Choose Logo
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setPackingSlipLogoFile(file);
                              if (file) setRemovePackingSlipLogo(false);
                            }}
                          />
                        </label>
                      </div>
                      <div className="flex items-center gap-3">
                        {packingSlipLogoUrl && !removePackingSlipLogo && (
                          <button
                            type="button"
                            onClick={() => {
                              setRemovePackingSlipLogo(true);
                              setPackingSlipLogoFile(null);
                            }}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove Current Logo
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleSavePackingSlipLogo}
                          disabled={savingPackingSlipLogo || (!packingSlipLogoFile && !removePackingSlipLogo)}
                          className="rounded-md border border-brand-gold bg-brand-gold px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-gold/90 disabled:opacity-50"
                        >
                          {savingPackingSlipLogo ? 'Saving...' : 'Save Logo'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Contact your company admin if this logo needs to be updated.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'email' && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <div>
            <h2 className="text-lg font-medium text-brand-navy">Company Email Preferences</h2>
            <p className="mt-1 text-sm text-gray-500">
              Controls which notification types go to the primary and
              additional notification emails above. Individual users can
              override these for their own inbox from their <em>My Settings</em>{' '}
              page.
            </p>
          </div>
          <div className="space-y-3">
            {NOTIFICATION_KEYS.map(([key, label]) => (
              <label key={key} className="flex items-center text-sm text-gray-700">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={companyForm.notificationPreferences[key] !== false}
                  onChange={(e) => handleNotificationPrefChange(key, e.target.checked)}
                  className="mr-3 rounded border-gray-300 text-brand-gold focus:ring-brand-gold disabled:opacity-60"
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'receiving' && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <div>
            <h2 className="text-lg font-medium text-brand-navy">Default Receiving Instructions</h2>
            <p className="mt-1 text-sm text-gray-500">
              Defaults for new ASNs and manifests. You can still change them
              on each individual inbound request.
            </p>
          </div>
          <div className="space-y-3">
            {RECEIVING_INSTRUCTIONS.map(([key, label]) => (
              <label key={key} className="flex items-center text-sm text-gray-700">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={companyForm.receivingPreferences[key] === true}
                  onChange={(e) => handleReceivingPrefChange(key, e.target.checked)}
                  className="mr-3 rounded border-gray-300 text-brand-gold focus:ring-brand-gold disabled:opacity-60"
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'contracts' && isCustomerAdmin && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-brand-navy mb-4">Contracts</h2>
          {contractsLoading ? (
            <LoadingSpinner />
          ) : contracts.length === 0 ? (
            <p className="text-sm text-gray-500">No contracts available.</p>
          ) : (
            <div className="space-y-4">
              {contracts.map((contract) => (
                <div key={contract.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-2">
                        <LuFileText className="h-5 w-5 text-gray-400" />
                        <h3 className="text-base font-medium text-brand-navy">{contract.title}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGES[contract.status] || STATUS_BADGES.active}`}>
                          {contract.status}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500">
                        <span>{CONTRACT_TYPES[contract.contract_type] || contract.contract_type}</span>
                        {contract.start_date && (
                          <span className="flex items-center">
                            <LuCalendar className="h-3.5 w-3.5 mr-1" />
                            {contract.start_date}
                            {contract.end_date ? ` - ${contract.end_date}` : ''}
                          </span>
                        )}
                        {contract.auto_renew && (
                          <span className="flex items-center text-brand-gold">
                            <LuRefreshCw className="h-3.5 w-3.5 mr-1" />
                            Auto-renews
                          </span>
                        )}
                      </div>
                    </div>
                    {contract.signedFileUrl && (
                      <a
                        href={contract.signedFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center rounded-md bg-brand-gold/10 px-3 py-1.5 text-sm font-medium text-brand-gold hover:bg-brand-gold/20"
                      >
                        <LuFileText className="h-4 w-4 mr-1.5" />
                        {contract.file_name || 'Download'}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'billing' && isCustomerAdmin && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-brand-navy">Billing Rates</h2>
            <p className="mt-1 text-sm text-gray-500">
              Your current service and storage rates, grouped by category.
              Read-only -- contact your account manager to request changes.
            </p>
          </div>

          {/* Flat-rate storage agreement banner. When an agreement
              is active, storage is billed at the flat monthly amount
              regardless of pallet count, and the per-pallet storage
              category below is hidden. */}
          {isFlatRateStorage && storageAgreement && (
            <div className="mb-6 rounded-lg border border-brand-gold bg-brand-gold/5 p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">
                Flat-Rate Storage Agreement
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <div>
                  <span className="text-2xl font-bold text-brand-navy">
                    {formatMoney(storageAgreement.monthlyAmount)}
                  </span>
                  <span className="text-sm text-gray-600"> / month</span>
                </div>
                <div className="text-sm text-gray-700">
                  {Number(storageAgreement.sqft).toLocaleString()} sqft rented
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-600">
                Per-pallet storage rates do not apply while this agreement is active.
                Handling and labour rates below still apply normally.
              </p>
            </div>
          )}

          {feesLoading ? (
            <LoadingSpinner />
          ) : fees.length === 0 ? (
            <p className="text-sm text-gray-500">No rates configured.</p>
          ) : (
            <div className="space-y-8">
              {feesByWarehouse.map((group, idx) => (
                <div key={idx}>
                  <h3 className="text-sm font-semibold text-brand-navy mb-3">
                    {group.warehouseName}
                    {group.warehouseCode ? (
                      <span className="ml-2 text-xs text-gray-500">({group.warehouseCode})</span>
                    ) : null}
                  </h3>

                  {group.categories.map((cat) => {
                    const isStorage = cat.label === 'Storage';
                    // Hide the per-pallet Storage category when the
                    // customer is on a flat-rate agreement -- the
                    // agreement card above already tells them what
                    // they're paying.
                    if (isStorage && isFlatRateStorage) return null;
                    return (
                      <div key={cat.label} className="mb-5">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          {cat.label}
                        </h4>
                        <div className="overflow-x-auto border border-gray-200 rounded-md">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left font-medium text-gray-600">Fee</th>
                                {isStorage && (
                                  <th className="px-4 py-2 text-left font-medium text-gray-600">Storage Class</th>
                                )}
                                <th className="px-4 py-2 text-right font-medium text-gray-600">Rate</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-600">Billing Unit</th>
                                <th className="px-4 py-2 text-left font-medium text-gray-600">Schedule</th>
                                {isStorage && (
                                  <>
                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Volume Discount</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Long-term Discount</th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {cat.rows.map((fee) => {
                                const scMeta = STORAGE_CLASSES.find((sc) => sc.value === (fee.storage_class || 'standard'));
                                return (
                                  <tr key={fee.id}>
                                    <td className="px-4 py-2 text-gray-900">
                                      <div className="font-medium">{fee._label}</div>
                                      {fee._description && (
                                        <div className="text-xs text-gray-500 mt-0.5">{fee._description}</div>
                                      )}
                                    </td>
                                    {isStorage && (
                                      <td className="px-4 py-2 text-gray-700">
                                        {scMeta?.label || humanize(fee.storage_class || 'standard')}
                                      </td>
                                    )}
                                    <td className="px-4 py-2 text-right text-gray-900 font-medium">{formatMoney(fee.fee_amount)}</td>
                                    <td className="px-4 py-2 text-gray-700">{humanize(fee.billing_unit)}</td>
                                    <td className="px-4 py-2 text-gray-700">{humanize(fee.fee_schedule)}</td>
                                    {isStorage && (
                                      <>
                                        <td className="px-4 py-2 text-gray-700">
                                          {fee.volume_discount_rate && fee.volume_discount_threshold
                                            ? `${formatMoney(fee.volume_discount_rate)} when ${fee.volume_discount_threshold}+ pallets`
                                            : <span className="text-gray-400">--</span>}
                                        </td>
                                        <td className="px-4 py-2 text-gray-700">
                                          {fee.longterm_discount_rate && fee.longterm_discount_months
                                            ? `${formatMoney(fee.longterm_discount_rate)} after ${fee.longterm_discount_months} months`
                                            : <span className="text-gray-400">--</span>}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'warehouses' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-brand-navy">Warehouses</h2>
            <p className="mt-1 text-sm text-gray-500">
              Warehouses your company has access to. Use these addresses
              when arranging inbound shipments or passing them to a
              forwarder.
            </p>
          </div>
          {warehousesLoading ? (
            <LoadingSpinner />
          ) : accessibleWarehouses.length === 0 ? (
            <p className="text-sm text-gray-500">
              Your company does not currently have access to any warehouses.
              Contact your account manager if this looks wrong.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {accessibleWarehouses.map((wh) => (
                <div key={wh.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-base font-medium text-brand-navy">{wh.name}</h3>
                      {wh.code && <p className="text-xs text-gray-500">Code: {wh.code}</p>}
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-700 whitespace-pre-line">
                    {[wh.address_line1, wh.address_line2].filter(Boolean).join('\n')}
                    {'\n'}
                    {[wh.city, wh.state_province, wh.postal_code].filter(Boolean).join(', ')}
                    {wh.country ? `\n${wh.country}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerProfile;
